import React, { useState } from 'react';
import s from '../../styles/corporate/Corporate.module.css';
import { PILLARS } from './data';
import { useScrollProgress } from './hooks';

/** 제목은 두 줄 — 아랫줄은 금색으로 채워진다 */
const TITLE_LINES = [
  ['성장의', '경험을', '기술로,'],
  ['연결의', '가치를', '미래로.'],
];
const TOTAL_WORDS = TITLE_LINES.flat().length;

/** 톱니 세 개(기업·사람·사회)가 도는 그림 — 라벨에 올리면 그 톱니만 남는다 */
const GEAR =
  'M57.96,-15.53 L59.36,-8.76 L75.73,-6.36 L75.73,6.36 L59.36,8.76 L57.96,15.53 L55.79,22.09 L68.77,32.36 L62.41,43.37 L47.02,37.27 L42.43,42.43 L37.27,47.02 L43.37,62.41 L32.36,68.77 L22.09,55.79 L15.53,57.96 L8.76,59.36 L6.36,75.73 L-6.36,75.73 L-8.76,59.36 L-15.53,57.96 L-22.09,55.79 L-32.36,68.77 L-43.37,62.41 L-37.27,47.02 L-42.43,42.43 L-47.02,37.27 L-62.41,43.37 L-68.77,32.36 L-55.79,22.09 L-57.96,15.53 L-59.36,8.76 L-75.73,6.36 L-75.73,-6.36 L-59.36,-8.76 L-57.96,-15.53 L-55.79,-22.09 L-68.77,-32.36 L-62.41,-43.37 L-47.02,-37.27 L-42.43,-42.43 L-37.27,-47.02 L-43.37,-62.41 L-32.36,-68.77 L-22.09,-55.79 L-15.53,-57.96 L-8.76,-59.36 L-6.36,-75.73 L6.36,-75.73 L8.76,-59.36 L15.53,-57.96 L22.09,-55.79 L32.36,-68.77 L43.37,-62.41 L37.27,-47.02 L42.43,-42.43 L47.02,-37.27 L62.41,-43.37 L68.77,-32.36 L55.79,-22.09 Z';
const GEAR_SMALL =
  'M40.41,-14.71 L42.18,-8.35 L58.63,-6.58 L58.63,6.58 L42.18,8.35 L40.41,14.71 L37.68,20.72 L49.14,32.65 L40.69,42.73 L26.94,33.51 L21.5,37.24 L15.55,40.09 L16.66,56.6 L3.7,58.88 L-0.9,42.99 L-7.47,42.35 L-13.86,40.71 L-23.62,54.07 L-35.01,47.49 L-28.32,32.35 L-32.94,27.64 L-36.78,22.28 L-52.85,26.23 L-57.35,13.87 L-42.49,6.58 L-43,0 L-42.49,-6.58 L-57.35,-13.87 L-52.85,-26.23 L-36.78,-22.28 L-32.94,-27.64 L-28.32,-32.35 L-35.01,-47.49 L-23.62,-54.07 L-13.86,-40.71 L-7.47,-42.35 L-0.9,-42.99 L3.7,-58.88 L16.66,-56.6 L15.55,-40.09 L21.5,-37.24 L26.94,-33.51 L40.69,-42.73 L49.14,-32.65 L37.68,-20.72 Z';

const RINGS = [
  { key: '0', label: '기업', x: 220, y: 96, labelY: 102, fill: '#a8875e' },
  { key: '1', label: '사람', x: 116.94, y: 274.5, labelY: 280.5, fill: '#1d1c1a' },
  { key: '2', label: '사회', x: 323.06, y: 274.5, labelY: 280.5, fill: '#cdb894' },
];

const CorpAbout = () => {
  const [titleRef, progress] = useScrollProgress();
  const [hovered, setHovered] = useState(null);

  const lit = Math.floor(progress * TOTAL_WORDS);
  let index = -1;

  return (
    <section className={`${s.wrap} ${s.section}`} id="about">
      <div className={s.aboutTop}>
        <h2 className={s.aboutTitle} ref={titleRef}>
          {TITLE_LINES.map((line, li) => (
            <React.Fragment key={line.join(' ')}>
              {li ? <br /> : null}
              {line.map((word) => {
                index += 1;
                const on = index < lit;
                return (
                  <span
                    key={`${word}-${index}`}
                    className={`${s.word} ${on ? (li ? s.wordGold : s.wordOn) : ''}`}
                  >
                    {word}{' '}
                  </span>
                );
              })}
            </React.Fragment>
          ))}
        </h2>

        <div className={`${s.aboutNote} ${s.reveal}`} data-delay="150">
          <div className={s.rule} aria-hidden="true" />
          <p className={s.aboutText}>
            오퍼그룹은 다양한 기업과의 협업과 운영 경험을 기반으로 B2B 솔루션을 발전시켜 왔습니다.
          </p>
          <p className={s.aboutText}>
            이제 그 경험을 AI 연구개발과 데이터 고도화에 연결해 더 효율적인 기업 환경을 만들어가고자
            합니다.
          </p>
        </div>
      </div>

      <div className={s.aboutGrid}>
        <div className={`${s.rings} ${s.reveal} ${s.revealScale}`}>
          <svg viewBox="0 0 440 370" role="img" aria-label="기업·사람·사회를 잇는 오퍼그룹">
            <g transform="translate(220,215) rotate(10)">
              <g className={s.ringGroupSlow}>
                <path d={GEAR_SMALL} fill="#e8e1d5" />
                <circle r="26" fill="#faf9f6" />
              </g>
            </g>

            {RINGS.map((ring) => (
              <g transform={`translate(${ring.x},${ring.y})`} key={ring.key}>
                <g
                  className={`${s.ring} ${hovered && hovered !== ring.key ? s.ringDim : ''}`}
                >
                  <g className={s.ringGroup}>
                    <path d={GEAR} fill={ring.fill} />
                    <circle r="40" fill="#faf9f6" />
                  </g>
                </g>
              </g>
            ))}

            <text
              x="220"
              y="219"
              textAnchor="middle"
              fontSize="10"
              fontWeight="700"
              letterSpacing="-.2"
              fill="#a8875e"
            >
              오퍼그룹
            </text>

            {RINGS.map((ring) => (
              <text
                key={ring.key}
                x={ring.x}
                y={ring.labelY}
                textAnchor="middle"
                fontSize="17"
                fontWeight="700"
                fill="#1d1c1a"
                onMouseEnter={() => setHovered(ring.key)}
                onMouseLeave={() => setHovered(null)}
              >
                {ring.label}
              </text>
            ))}
          </svg>
        </div>

        <div className={s.pillars}>
          {PILLARS.map((item, i) => (
            <div className={`${s.pillar} ${s.reveal}`} data-delay={i * 100} key={item.no}>
              <span className={s.mono} translate="no">
                {item.no}
              </span>
              <div>
                <div className={s.pillarTitle}>{item.title}</div>
                <div className={s.pillarBody}>{item.body}</div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default CorpAbout;
